import type { DefaultProps } from "@aerstudios/aerated-types/types";
import * as Tooltip from "@radix-ui/react-tooltip";
import cx from "clsx";
import React, { type ForwardedRef } from "react";
import * as styles from "./AerTooltip.module.scss";

const normalisedStyles = "default" in styles ? styles.default : styles;

export const AerTooltipTrigger = Tooltip.Trigger;

export const AerTooltipArrow = React.forwardRef(
	(
		{ className, ...rest }: Tooltip.TooltipArrowProps,
		ref: ForwardedRef<SVGSVGElement>,
	) => (
		<Tooltip.Arrow
			{...rest}
			className={cx(normalisedStyles.arrow, className)}
			ref={ref}
		/>
	),
);

export type AerTooltipContentProps = DefaultProps<"div"> &
	Tooltip.TooltipContentProps & { extractToPortal?: boolean };
export const AerTooltipContent = React.forwardRef(
	(
		{
			children,
			className,
			extractToPortal = false,
			...rest
		}: AerTooltipContentProps,
		ref: ForwardedRef<HTMLDivElement>,
	) => {
		if (extractToPortal) {
			return (
				<Tooltip.Portal>
					<Tooltip.Content
						{...rest}
						className={cx(normalisedStyles.content, className)}
						ref={ref}
					>
						{children}
					</Tooltip.Content>
				</Tooltip.Portal>
			);
		}

		return (
			<Tooltip.Content
				{...rest}
				className={cx(normalisedStyles.content, className)}
				ref={ref}
			>
				{children}
			</Tooltip.Content>
		);
	},
);

// todo, decide what to do about the provider - is this a pattern we want to use or should we work around it?
export type AerTooltipProps = DefaultProps<"div"> &
	Tooltip.TooltipProps &
	Tooltip.TooltipProviderProps;
/**
 * A tooltip is a popup that displays *short* information related to an element when the element receives keyboard focus or the mouse hovers over it (**NOTE:** this means that it must be an interactive element to receive keyboard focus). It disappears when Escape is pressed or on mouse out. This implementation is a wrapper around https://www.radix-ui.com/docs/primitives/components/tooltip
 *
 * Tooltip widgets *do not receive focus*; A dialog that contains focusable elements can be made using a non-modal dialog (TODO, make dialog component).
 *
 * ### Other considerations
 * There are some situations where one might want to provide a visual tooltip, but where the content is already available to assistive technology users. In this case, using a non-interactive element (which cannot receive focus) with a tooltip could be appropriate. For example, a tooltip could be used to provide a visual label for text which is only visually truncated.
 *
 * By the same token, disabled buttons cannot have tooltips, as they cannot receive focus. Consequently, one can work around this by wrapping the button in a `<span tabIndex={0}>` and applying the tooltip to that (the tab index adds the span to the focus order). You must also ensure to disable the point events on the button.
 *
 * ### See also
 * https://www.w3.org/WAI/ARIA/apg/patterns/tooltip/
 * https://www.radix-ui.com/docs/primitives/components/tooltip
 */
export const AerTooltip = ({
	className,
	children,
	...rest
}: AerTooltipProps) => {
	return (
		<Tooltip.Provider>
			<Tooltip.Root {...rest}>{children}</Tooltip.Root>
		</Tooltip.Provider>
	);
};
